// Employer subscription plans ("gói"). The RUNTIME source of truth is the
// SubscriptionPlan table (admin can edit price / duration / quotas without a
// deploy). This catalog holds the stable plan CODES + the DEFAULT values used
// to seed that table and as the FREE fallback when an org has no active
// subscription. Do not read these numbers for gating live orgs — read the DB
// row; read these only for seeding and for the implicit FREE baseline.

export const PLAN_CODES = ["FREE", "PRO", "BUSINESS"] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

// The next tier up from a plan (for "nâng cấp X" prompts). BUSINESS is the top
// tier → null (nothing to upsell). Display label is the marketing name, not the
// machine code.
const NEXT_PLAN: Record<PlanCode, { code: PlanCode; label: string } | null> = {
  FREE: { code: "PRO", label: "Pro" },
  PRO: { code: "BUSINESS", label: "Business" },
  BUSINESS: null,
};

export function nextPlanUp(
  code: PlanCode,
): { code: PlanCode; label: string } | null {
  return NEXT_PLAN[code];
}

export interface PlanDefaults {
  code: PlanCode;
  name: string;
  priceVnd: number;
  /** Period length in days. 0 = never expires (FREE). */
  durationDays: number;
  /** "Lượt săn ứng viên" per period. 0 = cannot source. */
  sourcingQuota: number;
  /** Concurrent OPEN postings allowed. */
  maxConcurrentPostings: number;
  /** Free boosts per period (extra boosts bought à la carte). */
  boostQuota: number;
  sortOrder: number;
}

// Default plan catalog — seeded into SubscriptionPlan on first migrate.
// Numbers per product spec:
//   Free  : no sourcing, 1 live posting, boost bought one-off
//   Pro   : 30 sourcing/period, 5 live postings, 1 free boost/period
//   Biz   : 200 sourcing/period, 20 live postings, 5 free boosts/period
export const PLAN_DEFAULTS: Record<PlanCode, PlanDefaults> = {
  FREE: {
    code: "FREE",
    name: "Miễn phí",
    priceVnd: 0,
    durationDays: 0,
    sourcingQuota: 0,
    maxConcurrentPostings: 1,
    boostQuota: 0,
    sortOrder: 0,
  },
  PRO: {
    code: "PRO",
    name: "Pro",
    priceVnd: 250_000,
    durationDays: 30,
    sourcingQuota: 30,
    maxConcurrentPostings: 5,
    boostQuota: 1,
    sortOrder: 1,
  },
  BUSINESS: {
    code: "BUSINESS",
    name: "Business",
    priceVnd: 1_200_000,
    durationDays: 30,
    sourcingQuota: 200,
    maxConcurrentPostings: 20,
    boostQuota: 5,
    sortOrder: 2,
  },
};

/** The implicit plan an org falls back to with no active (or expired) sub. */
export const FREE_PLAN_DEFAULTS = PLAN_DEFAULTS.FREE;

// À-la-carte top-up packs bought on top of a plan (Pro+). Add to extraSourcing
// / extraBoost, which do NOT reset each period. Stable codes so the buy modal
// + purchase records reference them. Prices are the current spec; edit here.
export interface TopupPack {
  code: string;
  /** How many extra units this pack grants. */
  quantity: number;
  priceVnd: number;
}

// Extra "lượt săn ứng viên".
export const SOURCING_PACKS: TopupPack[] = [
  { code: "SRC_10", quantity: 10, priceVnd: 100_000 },
  { code: "SRC_30", quantity: 30, priceVnd: 250_000 },
  { code: "SRC_100", quantity: 100, priceVnd: 700_000 },
];

// Extra boosts — one-off boost costs 50k (per spec).
export const BOOST_PACKS: TopupPack[] = [
  { code: "BST_1", quantity: 1, priceVnd: 50_000 },
  { code: "BST_5", quantity: 5, priceVnd: 200_000 },
];

export function findTopupPack(
  kind: "SOURCING" | "BOOST",
  code: string,
): TopupPack | undefined {
  const packs = kind === "SOURCING" ? SOURCING_PACKS : BOOST_PACKS;
  return packs.find((p) => p.code === code);
}
